import { useState, useEffect, useRef } from "react";
import { Mic, Play, Pause, Trash2, Download, Globe, RefreshCw, Search } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

export const PodcastLibrary = ({ authToken }) => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => { fetchEpisodes(); }, []);

  const fetchEpisodes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/podcast/library`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEpisodes(data.episodes || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const deleteEpisode = async (episodeId) => {
    if (!window.confirm("Delete this AI-generated podcast?")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/podcast/${episodeId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) setEpisodes(prev => prev.filter(e => e.episode_id !== episodeId));
    } catch (err) { console.error(err); }
  };

  const togglePublish = async (episodeId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/podcast/${episodeId}/publish`, {
        method: "PUT", headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEpisodes(prev => prev.map(e => e.episode_id === episodeId ? { ...e, published: data.published } : e));
      }
    } catch (err) { console.error(err); }
  };

  const playEpisode = (ep) => {
    if (playingId === ep.episode_id) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(ep.audio_url);
      audioRef.current = audio;
      audio.play();
      audio.onended = () => setPlayingId(null);
      setPlayingId(ep.episode_id);
    }
  };

  const downloadEpisode = (ep) => {
    const a = document.createElement("a");
    a.href = ep.audio_url;
    a.download = `${ep.title || "podcast"}.mp3`;
    a.click();
  };

  const filtered = episodes.filter(e =>
    e.title?.toLowerCase().includes(search.toLowerCase()) ||
    e.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="podcast-library">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Mic className="w-5 h-5 text-violet-400" /> Podcast Library
          <span className="text-xs text-white/30 ml-1">({episodes.length} episodes)</span>
        </h3>
        <button onClick={fetchEpisodes} className="text-white/40 hover:text-white transition p-2" data-testid="podcast-library-refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {episodes.length > 3 && (
        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search podcasts..."
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-violet-500/50"
            data-testid="podcast-library-search" />
        </div>
      )}

      {loading ? (
        <div className="text-center py-8"><div className="w-6 h-6 border-2 border-white/20 border-t-violet-500 rounded-full animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white/[0.02] border border-white/[0.06] rounded-2xl">
          <Mic className="w-10 h-10 text-white/10 mx-auto mb-3" />
          <p className="text-white/30 text-sm">{search ? "No matching podcasts" : "No AI-generated podcasts yet"}</p>
          <p className="text-white/20 text-xs mt-1">Use the Podcast Generator to create your first episode</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(ep => (
            <div key={ep.episode_id} className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4" data-testid={`podcast-episode-${ep.episode_id}`}>
              <div className="flex items-center gap-3">
                {/* Play Button */}
                <button onClick={() => playEpisode(ep)} disabled={!ep.audio_url}
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition ${playingId === ep.episode_id ? "bg-violet-500/30 text-violet-300" : "bg-white/5 text-white/40 hover:bg-violet-500/20 hover:text-violet-300"}`}
                  data-testid={`podcast-play-${ep.episode_id}`}>
                  {playingId === ep.episode_id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                </button>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm truncate">{ep.title}</p>
                  <div className="flex items-center gap-3 text-[11px] text-white/30 mt-0.5">
                    {ep.ai_metadata?.voice && <span className="text-violet-400/60">{ep.ai_metadata.voice}</span>}
                    {ep.ai_metadata?.tone && <span>{ep.ai_metadata.tone}</span>}
                    {ep.ai_metadata?.length && <span>{ep.ai_metadata.length.replace("_", " ")}</span>}
                    <span>{new Date(ep.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => togglePublish(ep.episode_id)}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition ${ep.published ? "bg-green-500/20 text-green-400" : "bg-white/5 text-white/40 hover:bg-white/10"}`}
                    title={ep.published ? "Unpublish" : "Publish"}>
                    <Globe className="w-3 h-3 inline mr-1" />{ep.published ? "Live" : "Draft"}
                  </button>
                  {ep.audio_url && (
                    <button onClick={() => downloadEpisode(ep)} className="text-white/20 hover:text-cyan-400 transition p-1.5" title="Download">
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => deleteEpisode(ep.episode_id)} className="text-white/20 hover:text-red-400 transition p-1.5" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
